// ─── White-Label Theme Presets ────────────────────────────────────────────────
// CSS variable overrides per brand, applied on top of the base definitions
// in src/assets/scss/main.scss.
//
// All variable names here MUST match what is already defined in main.scss —
// do not invent new variable names. The values here can be updated per brand
// without touching SCSS.
//
// HOW TO USE:
//   import { getThemePreset, getCSSVars } from '@/config/theme'
//   // Apply all vars for a preset + mode:
//   const vars = getCSSVars('dougjonesfor scheme-based sets use getThemePreset().
//
// APPLYING VARS:
//   Object.entries(vars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v))
//
// NOTE: The Change Network and UniMiddle colors are placeholders — update later per brand guidelines.
// ─────────────────────────────────────────────────────────────────────────────

const themes = {

  // ── Default (The Change Network / current app colors from main.scss) ─────────────────
  default: {
    light: {
      // Primary interactive (buttons, tabs, accents)
      '--color-brand-secondary':                 '#B44C6C',
      '--color-accent-primary':             '#001871',
      '--color-brand-secondary-hover':           '#B44C6C',
      '--color-brand-secondary-hover-v2':       '#001871e0',
      '--color-brand-secondary-active':          '#B44C6C',
      '--color-brand-secondary-active-v2':      '#001871',
      '--color-interactive-primary-disabled-bg':'#00187187',
      '--color-primary-button-bg':              '#B44C6C',

      // Text colors (brand-tinted)
      '--color-text-black-white':               '#fff',
      '--color-text-black-white-v2':           '#001871',
      '--color-text-theme-v2':                 '#001871',
      '--color-text-green-white':               '#001871',
      '--color-text-civic':               '#B44C6C',
      '--color-text-value-label':         '#4849A1',
      '--color-welcome-text':                   '#843688',
      '--color-subtitle-login':                 '#4849A1',
      '--color-member-text':                    '#4849A1',
      '--color-tip-text':                 '#646464',
      '--color-tip-text-v2':             '#646464',

      // Borders and accents
      '--color-dot':                           '#4849A1',
      '--color-dot-v2':                       '#646464',
      '--color-input-text':                     '#B44C6C',
      '--color-input-field-border':             '#F4E6FF',
      '--color-border-leftbar-transparent':     '#F4E6FF',
      '--color-reaction-bg':                    '#c4cacf',
      '--color-border-reaction-bar':            '#F4E6FF',
      '--color-outline-reaction-bar':           '#F4E6FF',
      '--color-border':                   '#005D9E',
      '--color-border-v2':               '#001871',

      // Tabs
      '--color-tab-text':                 '#B44C6C',
      '--color-tab-inactive-text':        '#B44C6C',
      '--color-active-tab-text':                '#B44C6C',
      '--color-active-tab-text-v2':            '#001871',
      '--color-inactive-tab-text':              '#843688',
      '--color-inactive-tab-text-v2':          '#628BA8',

      // Buttons & dropdowns
      '--color-join-button':                   '#001871',
      '--color-join-button-hover':             '#001871',
      '--color-post-dropdown':                  '#B44C6C',
      '--color-post-dropdown-v2':              '#001871',
      '--color-edit-button':                   '#B44C6C',
      '--color-right-sidebar-protip':           '#B44C6C',
      '--color-right-sidebar-protip-v2':       '#001871',
      '--color-dropdown-text':                  '#001871',
      '--color-dropdown-icon':                  '#005D9E',

      // Container heads & navigation
      '--color-container-head':                 '#A888A2',
      '--color-container-head-v2':             '#001871',

      // Sidebar / groups
      '--color-quick-link-icon-bg':             '#001871',
      '--color-green-light-white':              '#001871',
      '--color-group-title-text':               '#001871',
      '--color-status-box-v2':                 '#005D9E',

      // Backgrounds & layout
      '--color-button-border':            '#326295',
      '--color-theme-wise-background':          '#FFFFFF',
      '--color-theme-background-v2':      '#F5F6F7',
      '--color-login-bg':                       '#FFFFFF',
      '--color-cell-field-border-v2':          '#326295',
      '--color-cell-field-bg-v2':              '#fff',

      // Color scheme switcher
      '--color-switcher-icon-dark':       '#FFAD00',
      '--color-switcher-icon-light':      '#001871',

      // Notifications
      '--color-notification-border-unread-light-v2': '#001871eb',
    },
    dark: {
      '--color-brand-secondary':                 '#B44C6C',
      '--color-accent-primary':             '#FFAD00',
      '--color-brand-secondary-hover':           '#B44C6C',
      '--color-brand-secondary-hover-v2':       '#FFAD00',
      '--color-brand-secondary-active':          '#B44C6C',
      '--color-brand-secondary-active-v2':      '#FFAD00',
      '--color-interactive-primary-disabled-bg':'#FFAD0087',
      '--color-primary-button-bg':              '#B44C6C',

      '--color-text-black-white':               '#000',
      '--color-text-black-white-v2':           '#FFAD00',
      '--color-text-theme-v2':                 '#FFAD00',
      '--color-text-green-white':               '#fff',
      '--color-text-civic':               '#ffffff',
      '--color-text-value-label':         '#BCA3DA',
      '--color-welcome-text':                   '#B44C6C',
      '--color-subtitle-login':                 '#BCA3DA',
      '--color-member-text':                    '#A7AFC7',
      '--color-tip-text':                 '#FCFCFD',
      '--color-tip-text-v2':             '#FCFCFD',

      '--color-dot':                           '#A7AFC7',
      '--color-dot-v2':                       '#B9CAFF',
      '--color-input-text':                     '#A888A2',
      '--color-input-field-border':             '#84858C',
      '--color-border-leftbar-transparent':     '#54555C',
      '--color-reaction-bg':                    '#141414',
      '--color-border-reaction-bar':            '#2C2C2C',
      '--color-outline-reaction-bar':           '#2C2C2C',
      '--color-border':                   '#FFFCF1',
      '--color-border-v2':               '#FFFCF1',

      '--color-tab-text':                 '#ffffff',
      '--color-tab-inactive-text':        '#ffffff',
      '--color-active-tab-text':                '#B44C6C',
      '--color-active-tab-text-v2':            '#FFAD00',
      '--color-inactive-tab-text':              '#843688',
      '--color-inactive-tab-text-v2':          '#C1C1C1',

      '--color-join-button':                   '#FFAD00',
      '--color-join-button-hover':             '#FFAD00',
      '--color-post-dropdown':                  '#FFAD00',
      '--color-post-dropdown-v2':              '#FFAD00',
      '--color-edit-button':                   '#A888A2',
      '--color-right-sidebar-protip':           '#FAFAFA',
      '--color-right-sidebar-protip-v2':       '#FFAD00',
      '--color-dropdown-text':                  '#fff',
      '--color-dropdown-icon':                  '#000',

      '--color-container-head':                 '#A888A2',
      '--color-container-head-v2':             '#FFAD00',

      '--color-quick-link-icon-bg':             '#FFAD00',
      '--color-green-light-white':              '#e4e4e7',
      '--color-group-title-text':               '#B9CAFF',
      '--color-status-box-v2':                 '#FFAD00',

      // Backgrounds & layout
      '--color-button-border':            '#FFFCF1',
      '--color-theme-wise-background':          '#3E4049',
      '--color-theme-background-v2':      '#282A31',
      '--color-login-bg':                       '#141414',
      '--color-cell-field-border-v2':          '#FFAD00',
      '--color-cell-field-bg-v2':              '#282A31',

      // Color scheme switcher
      '--color-switcher-icon-dark':       '#FFAD00',
      '--color-switcher-icon-light':      '#001871',

      // Notifications
      '--color-notification-border-unread-dark-v2': '#FFFCF1',
    },
  },

  // ── The Change Network ────────────────────────────────────────────────────────────
  // TODO: Replace placeholder values with actual The Change Network brand colors.
  // Current values mirror the default theme — update once brand guidelines are received.
  dougjoneslight: {
    light: {
      '--color-brand-secondary':                 '#B44C6C',
      '--color-accent-primary':             '#001871',
      '--color-brand-secondary-hover':           '#B44C6C',
      '--color-brand-secondary-hover-v2':       '#001871e0',
      '--color-brand-secondary-active':          '#B44C6C',
      '--color-brand-secondary-active-v2':      '#001871',
      '--color-interactive-primary-disabled-bg':'#00187187',
      '--color-primary-button-bg':              '#B44C6C',
      '--color-text-black-white':               '#fff',
      '--color-text-black-white-v2':           '#001871',
      '--color-text-theme-v2':                 '#001871',
      '--color-text-green-white':               '#001871',
      '--color-text-civic':               '#B44C6C',
      '--color-text-value-label':         '#4849A1',
      '--color-welcome-text':                   '#843688',
      '--color-subtitle-login':                 '#4849A1',
      '--color-member-text':                    '#4849A1',
      '--color-tip-text':                 '#646464',
      '--color-tip-text-v2':             '#646464',
      '--color-dot':                           '#4849A1',
      '--color-dot-v2':                       '#646464',
      '--color-input-text':                     '#B44C6C',
      '--color-input-field-border':             '#F4E6FF',
      '--color-border-leftbar-transparent':     '#F4E6FF',
      '--color-reaction-bg':                    '#c4cacf',
      '--color-border-reaction-bar':            '#F4E6FF',
      '--color-outline-reaction-bar':           '#F4E6FF',
      '--color-border':                   '#005D9E',
      '--color-border-v2':               '#001871',
      '--color-tab-text':                 '#B44C6C',
      '--color-tab-inactive-text':        '#B44C6C',
      '--color-active-tab-text':                '#B44C6C',
      '--color-active-tab-text-v2':            '#001871',
      '--color-inactive-tab-text':              '#843688',
      '--color-inactive-tab-text-v2':          '#628BA8',
      '--color-join-button':                   '#001871',
      '--color-join-button-hover':             '#001871',
      '--color-post-dropdown':                  '#B44C6C',
      '--color-post-dropdown-v2':              '#001871',
      '--color-edit-button':                   '#B44C6C',
      '--color-right-sidebar-protip':           '#B44C6C',
      '--color-right-sidebar-protip-v2':       '#001871',
      '--color-dropdown-text':                  '#001871',
      '--color-dropdown-icon':                  '#005D9E',
      '--color-container-head':                 '#A888A2',
      '--color-container-head-v2':             '#001871',
      '--color-quick-link-icon-bg':             '#001871',
      '--color-green-light-white':              '#001871',
      '--color-group-title-text':               '#001871',
      '--color-status-box-v2':                 '#005D9E',

      // Backgrounds & layout
      '--color-button-border':            '#326295',
      '--color-theme-wise-background':          '#FFFFFF',
      '--color-theme-background-v2':      '#F5F6F7',
      '--color-login-bg':                       '#FFFFFF',
      '--color-cell-field-border-v2':          '#326295',
      '--color-cell-field-bg-v2':              '#fff',

      // Color scheme switcher
      '--color-switcher-icon-dark':       '#FFAD00',
      '--color-switcher-icon-light':      '#001871',

      // Notifications
      '--color-notification-border-unread-light-v2': '#001871eb',
    },
    dark: {
      '--color-brand-secondary':                 '#B44C6C',
      '--color-accent-primary':             '#FFAD00',
      '--color-brand-secondary-hover':           '#B44C6C',
      '--color-brand-secondary-hover-v2':       '#FFAD00',
      '--color-brand-secondary-active':          '#B44C6C',
      '--color-brand-secondary-active-v2':      '#FFAD00',
      '--color-interactive-primary-disabled-bg':'#FFAD0087',
      '--color-primary-button-bg':              '#B44C6C',
      '--color-text-black-white':               '#000',
      '--color-text-black-white-v2':           '#FFAD00',
      '--color-text-theme-v2':                 '#FFAD00',
      '--color-text-green-white':               '#fff',
      '--color-text-civic':               '#ffffff',
      '--color-text-value-label':         '#BCA3DA',
      '--color-welcome-text':                   '#B44C6C',
      '--color-subtitle-login':                 '#BCA3DA',
      '--color-member-text':                    '#A7AFC7',
      '--color-tip-text':                 '#FCFCFD',
      '--color-tip-text-v2':             '#FCFCFD',
      '--color-dot':                           '#A7AFC7',
      '--color-dot-v2':                       '#B9CAFF',
      '--color-input-text':                     '#A888A2',
      '--color-input-field-border':             '#84858C',
      '--color-border-leftbar-transparent':     '#54555C',
      '--color-reaction-bg':                    '#141414',
      '--color-border-reaction-bar':            '#2C2C2C',
      '--color-outline-reaction-bar':           '#2C2C2C',
      '--color-border':                   '#FFFCF1',
      '--color-border-v2':               '#FFFCF1',
      '--color-tab-text':                 '#ffffff',
      '--color-tab-inactive-text':        '#ffffff',
      '--color-active-tab-text':                '#B44C6C',
      '--color-active-tab-text-v2':            '#FFAD00',
      '--color-inactive-tab-text':              '#843688',
      '--color-inactive-tab-text-v2':          '#C1C1C1',
      '--color-join-button':                   '#FFAD00',
      '--color-join-button-hover':             '#FFAD00',
      '--color-post-dropdown':                  '#FFAD00',
      '--color-post-dropdown-v2':              '#FFAD00',
      '--color-edit-button':                   '#A888A2',
      '--color-right-sidebar-protip':           '#FAFAFA',
      '--color-right-sidebar-protip-v2':       '#FFAD00',
      '--color-dropdown-text':                  '#fff',
      '--color-dropdown-icon':                  '#000',
      '--color-container-head':                 '#A888A2',
      '--color-container-head-v2':             '#FFAD00',
      '--color-quick-link-icon-bg':             '#FFAD00',
      '--color-green-light-white':              '#e4e4e7',
      '--color-group-title-text':               '#B9CAFF',
      '--color-status-box-v2':                 '#FFAD00',

      // Backgrounds & layout
      '--color-button-border':            '#FFFCF1',
      '--color-theme-wise-background':          '#3E4049',
      '--color-theme-background-v2':      '#282A31',
      '--color-login-bg':                       '#141414',
      '--color-cell-field-border-v2':          '#FFAD00',
      '--color-cell-field-bg-v2':              '#282A31',

      // Color scheme switcher
      '--color-switcher-icon-dark':       '#FFAD00',
      '--color-switcher-icon-light':      '#001871',

      // Notifications
      '--color-notification-border-unread-dark-v2': '#FFFCF1',
    },
  },

  // ── UniMiddle ─────────────────────────────────────────────────────────────
  // TODO: Replace placeholder values with actual UniMiddle brand colors.
  // Current values mirror the default theme — update once brand guidelines are received.
  unimiddle: {
    light: {
      '--color-brand-secondary':                 '#B44C6C',
      '--color-accent-primary':             '#001871',
      '--color-brand-secondary-hover':           '#B44C6C',
      '--color-brand-secondary-hover-v2':       '#001871e0',
      '--color-brand-secondary-active':          '#B44C6C',
      '--color-brand-secondary-active-v2':      '#001871',
      '--color-interactive-primary-disabled-bg':'#00187187',
      '--color-primary-button-bg':              '#B44C6C',
      '--color-text-black-white':               '#fff',
      '--color-text-black-white-v2':           '#001871',
      '--color-text-theme-v2':                 '#001871',
      '--color-text-green-white':               '#001871',
      '--color-text-civic':               '#B44C6C',
      '--color-text-value-label':         '#4849A1',
      '--color-welcome-text':                   '#843688',
      '--color-subtitle-login':                 '#4849A1',
      '--color-member-text':                    '#4849A1',
      '--color-tip-text':                 '#646464',
      '--color-tip-text-v2':             '#646464',
      '--color-dot':                           '#4849A1',
      '--color-dot-v2':                       '#646464',
      '--color-input-text':                     '#B44C6C',
      '--color-input-field-border':             '#F4E6FF',
      '--color-border-leftbar-transparent':     '#F4E6FF',
      '--color-reaction-bg':                    '#c4cacf',
      '--color-border-reaction-bar':            '#F4E6FF',
      '--color-outline-reaction-bar':           '#F4E6FF',
      '--color-border':                   '#005D9E',
      '--color-border-v2':               '#001871',
      '--color-tab-text':                 '#B44C6C',
      '--color-tab-inactive-text':        '#B44C6C',
      '--color-active-tab-text':                '#B44C6C',
      '--color-active-tab-text-v2':            '#001871',
      '--color-inactive-tab-text':              '#843688',
      '--color-inactive-tab-text-v2':          '#628BA8',
      '--color-join-button':                   '#001871',
      '--color-join-button-hover':             '#001871',
      '--color-post-dropdown':                  '#B44C6C',
      '--color-post-dropdown-v2':              '#001871',
      '--color-edit-button':                   '#B44C6C',
      '--color-right-sidebar-protip':           '#B44C6C',
      '--color-right-sidebar-protip-v2':       '#001871',
      '--color-dropdown-text':                  '#001871',
      '--color-dropdown-icon':                  '#005D9E',
      '--color-container-head':                 '#A888A2',
      '--color-container-head-v2':             '#001871',
      '--color-quick-link-icon-bg':             '#001871',
      '--color-green-light-white':              '#001871',
      '--color-group-title-text':               '#001871',
      '--color-status-box-v2':                 '#005D9E',

      // Backgrounds & layout
      '--color-button-border':            '#326295',
      '--color-theme-wise-background':          '#FFFFFF',
      '--color-theme-background-v2':      '#F5F6F7',
      '--color-login-bg':                       '#FFFFFF',
      '--color-cell-field-border-v2':          '#326295',
      '--color-cell-field-bg-v2':              '#fff',

      // Color scheme switcher
      '--color-switcher-icon-dark':       '#FFAD00',
      '--color-switcher-icon-light':      '#001871',

      // Notifications
      '--color-notification-border-unread-light-v2': '#001871eb',
    },
    dark: {
      '--color-brand-secondary':                 '#B44C6C',
      '--color-accent-primary':             '#FFAD00',
      '--color-brand-secondary-hover':           '#B44C6C',
      '--color-brand-secondary-hover-v2':       '#FFAD00',
      '--color-brand-secondary-active':          '#B44C6C',
      '--color-brand-secondary-active-v2':      '#FFAD00',
      '--color-interactive-primary-disabled-bg':'#FFAD0087',
      '--color-primary-button-bg':              '#B44C6C',
      '--color-text-black-white':               '#000',
      '--color-text-black-white-v2':           '#FFAD00',
      '--color-text-theme-v2':                 '#FFAD00',
      '--color-text-green-white':               '#fff',
      '--color-text-civic':               '#ffffff',
      '--color-text-value-label':         '#BCA3DA',
      '--color-welcome-text':                   '#B44C6C',
      '--color-subtitle-login':                 '#4849A1',
      '--color-member-text':                    '#A7AFC7',
      '--color-tip-text':                 '#FCFCFD',
      '--color-tip-text-v2':             '#FCFCFD',
      '--color-dot':                           '#A7AFC7',
      '--color-dot-v2':                       '#B9CAFF',
      '--color-input-text':                     '#A888A2',
      '--color-input-field-border':             '#D9E5EC',
      '--color-border-leftbar-transparent':     '#54555C',
      '--color-reaction-bg':                    '#141414',
      '--color-border-reaction-bar':            '#2C2C2C',
      '--color-outline-reaction-bar':           '#2C2C2C',
      '--color-border':                   '#7FCBD8',
      '--color-border-v2':               '#7FCBD8',
      '--color-tab-text':                 '#ffffff',
      '--color-tab-inactive-text':        '#ffffff',
      '--color-active-tab-text':                '#B44C6C',
      '--color-active-tab-text-v2':            '#FFAD00',
      '--color-inactive-tab-text':              '#843688',
      '--color-inactive-tab-text-v2':          '#C1C1C1',
      '--color-join-button':                   '#FFAD00',
      '--color-join-button-hover':             '#FFAD00',
      '--color-post-dropdown':                  '#FFAD00',
      '--color-post-dropdown-v2':              '#FFAD00',
      '--color-edit-button':                   '#A888A2',
      '--color-right-sidebar-protip':           '#FAFAFA',
      '--color-right-sidebar-protip-v2':       '#FFAD00',
      '--color-dropdown-text':                  '#fff',
      '--color-dropdown-icon':                  '#000',
      '--color-container-head':                 '#A888A2',
      '--color-container-head-v2':             '#FFAD00',
      '--color-quick-link-icon-bg':             '#FFAD00',
      '--color-green-light-white':              '#e4e4e7',
      '--color-group-title-text':               '#B9CAFF',
      '--color-status-box-v2':                 '#FFAD00',

      // Backgrounds & layout
      '--color-button-border':            '#FFFCF1',
      '--color-theme-wise-background':          '#3E4049',
      '--color-theme-background-v2':      '#282A31',
      '--color-login-bg':                       '#141414',
      '--color-cell-field-border-v2':          '#FFAD00',
      '--color-cell-field-bg-v2':              '#282A31',

      // Color scheme switcher
      '--color-switcher-icon-dark':       '#FFAD00',
      '--color-switcher-icon-light':      '#001871',

      // Notifications
      '--color-notification-border-unread-dark-v2': '#FFFCF1',
    },
  },

  // ── Allen Jackson ─────────────────────────────────────────────────────────
  // Allen Jackson brand colors
  // Light: Primary #1F3B5B, Gold #C8A45D, BG #F5F6F8, Text #1A1A1A, Secondary #6B7280, Borders #E2E5EA
  // Dark:  Primary #27496D, Gold #D4B06A, BG #121417, Surface #1B1F24, Text #F5F5F5, Secondary #A1A1AA, Borders #2B3138
  allenjackson: {
    light: {
      // Primary interactive — gold accent, navy primary
      '--color-brand-secondary':                 '#C8A45D',
      '--color-accent-primary':             '#1F3B5B',
      '--color-brand-secondary-hover':           '#B8944D',
      '--color-brand-secondary-hover-v2':       '#1F3B5Be0',
      '--color-brand-secondary-active':          '#C8A45D',
      '--color-brand-secondary-active-v2':      '#1F3B5B',
      '--color-interactive-primary-disabled-bg':'#1F3B5B87',
      '--color-primary-button-bg':              '#C8A45D',

      // Text colors
      '--color-text-black-white':               '#fff',
      '--color-text-black-white-v2':           '#1F3B5B',
      '--color-text-theme-v2':                 '#1F3B5B',
      '--color-text-green-white':               '#1F3B5B',
      '--color-text-civic':               '#1F3B5B',
      '--color-text-value-label':         '#6B7280',
      '--color-welcome-text':                   '#1F3B5B',
      '--color-subtitle-login':                 '#6B7280',
      '--color-member-text':                    '#6B7280',
      '--color-tip-text':                 '#6B7280',
      '--color-tip-text-v2':             '#6B7280',

      // Borders and accents
      '--color-dot':                           '#1F3B5B',
      '--color-dot-v2':                       '#6B7280',
      '--color-input-text':                     '#1F3B5B',
      '--color-input-field-border':             '#E2E5EA',
      '--color-border-leftbar-transparent':     '#E2E5EA',
      '--color-reaction-bg':                    '#F5F6F8',
      '--color-border-reaction-bar':            '#E2E5EA',
      '--color-outline-reaction-bar':           '#E2E5EA',
      '--color-border':                   '#1F3B5B',
      '--color-border-v2':               '#1F3B5B',

      // Tabs
      '--color-tab-text':                 '#1F3B5B',
      '--color-tab-inactive-text':        '#1F3B5B',
      '--color-active-tab-text':                '#1F3B5B',
      '--color-active-tab-text-v2':            '#1F3B5B',
      '--color-inactive-tab-text':              '#6B7280',
      '--color-inactive-tab-text-v2':          '#6B7280',

      // Buttons & dropdowns
      '--color-join-button':                   '#1F3B5B',
      '--color-join-button-hover':             '#1F3B5B',
      '--color-post-dropdown':                  '#1F3B5B',
      '--color-post-dropdown-v2':              '#1F3B5B',
      '--color-edit-button':                   '#1F3B5B',
      '--color-right-sidebar-protip':           '#1F3B5B',
      '--color-right-sidebar-protip-v2':       '#1F3B5B',
      '--color-dropdown-text':                  '#1F3B5B',
      '--color-dropdown-icon':                  '#1F3B5B',

      // Container heads & navigation
      '--color-container-head':                 '#1F3B5B',
      '--color-container-head-v2':             '#1F3B5B',

      // Sidebar / groups
      '--color-quick-link-icon-bg':             '#1F3B5B',
      '--color-green-light-white':              '#1F3B5B',
      '--color-group-title-text':               '#1F3B5B',
      '--color-status-box-v2':                 '#C8A45D',

      // Backgrounds & layout
      '--color-button-border':            '#1F3B5B',
      '--color-theme-wise-background':          '#FFFFFF',
      '--color-theme-background-v2':      '#F5F6F8',
      '--color-login-bg':                       '#FFFFFF',
      '--color-cell-field-border-v2':          '#1F3B5B',
      '--color-cell-field-bg-v2':              '#FFFFFF',

      // Color scheme switcher
      '--color-switcher-icon-dark':       '#D4B06A',
      '--color-switcher-icon-light':      '#1F3B5B',

      // Notifications
      '--color-notification-border-unread-light-v2': '#1F3B5Beb',
      '--color-new-input-field-border':         '#1F3B5B',
      '--color-header-bg-light-v2':           '#F5F6F8',  
      '--color-input-field-bg' :             '#F5F6F8', 
    },
    dark: {
      // Primary interactive — gold accent
      '--color-brand-secondary':                 '#D4B06A',
      '--color-accent-primary':             '#D4B06A',
      '--color-brand-secondary-hover':           '#D4B06A',
      '--color-brand-secondary-hover-v2':       '#D4B06A',
      '--color-brand-secondary-active':          '#D4B06A',
      '--color-brand-secondary-active-v2':      '#D4B06A',
      '--color-interactive-primary-disabled-bg':'#D4B06A87',
      '--color-primary-button-bg':              '#D4B06A',

      // Text colors
      '--color-text-black-white':               '#000',
      '--color-text-black-white-v2':           '#D4B06A',
      '--color-text-theme-v2':                 '#D4B06A',
      '--color-text-green-white':               '#F5F5F5',
      '--color-text-civic':               '#F5F5F5',
      '--color-text-value-label':         '#A1A1AA',
      '--color-welcome-text':                   '#D4B06A',
      '--color-subtitle-login':                 '#A1A1AA',
      '--color-member-text':                    '#A1A1AA',
      '--color-tip-text':                 '#F5F5F5',
      '--color-tip-text-v2':             '#F5F5F5',

      // Borders and accents
      '--color-dot':                           '#D4B06A',
      '--color-dot-v2':                       '#A1A1AA',
      '--color-input-text':                     '#D4B06A',
      '--color-input-field-border':             '#2B3138',
      '--color-border-leftbar-transparent':     '#2B3138',
      '--color-reaction-bg':                    '#121417',
      '--color-border-reaction-bar':            '#2B3138',
      '--color-outline-reaction-bar':           '#2B3138',
      '--color-border':                   '#D4B06A',
      '--color-border-v2':               '#D4B06A',

      // Tabs
      '--color-tab-text':                 '#F5F5F5',
      '--color-tab-inactive-text':        '#F5F5F5',
      '--color-active-tab-text':                '#D4B06A',
      '--color-active-tab-text-v2':            '#D4B06A',
      '--color-inactive-tab-text':              '#A1A1AA',
      '--color-inactive-tab-text-v2':          '#A1A1AA',

      // Buttons & dropdowns
      '--color-join-button':                   '#D4B06A',
      '--color-join-button-hover':             '#D4B06A',
      '--color-post-dropdown':                  '#D4B06A',
      '--color-post-dropdown-v2':              '#D4B06A',
      '--color-edit-button':                   '#D4B06A',
      '--color-right-sidebar-protip':           '#F5F5F5',
      '--color-right-sidebar-protip-v2':       '#D4B06A',
      '--color-dropdown-text':                  '#F5F5F5',
      '--color-dropdown-icon':                  '#F5F5F5',

      // Container heads & navigation
      '--color-container-head':                 '#D4B06A',
      '--color-container-head-v2':             '#D4B06A',

      // Sidebar / groups
      '--color-quick-link-icon-bg':             '#D4B06A',
      '--color-green-light-white':              '#A1A1AA',
      '--color-group-title-text':               '#D4B06A',
      '--color-status-box-v2':                 '#D4B06A',

      // Backgrounds & layout
      '--color-button-border':            '#D4B06A',
      '--color-theme-wise-background':          '#1B1F24',
      '--color-theme-background-v2':      '#121417',
      '--color-login-bg':                       '#121417',
      '--color-cell-field-border-v2':          '#D4B06A',
      '--color-cell-field-bg-v2':              '#1B1F24',

      // Color scheme switcher
      '--color-switcher-icon-dark':       '#D4B06A',
      '--color-switcher-icon-light':      '#27496D',

      // Notifications
      '--color-notification-border-unread-dark-v2': '#D4B06A',

       '--color-new-input-field-border':         '#D4B06A',
       '--color-header-bg-light-v2':           '#121417', 
       '--color-input-field-bg' :             '#1B1F24',  
    },
  },
};

export default themes;

// Returns the full preset object { light: {}, dark: {} } for a brand name.
export const getThemePreset = (presetName) => themes[presetName] ?? themes['default'];

// Returns a flat CSS-var map for the given preset name + color scheme.
// Pass the result to document.documentElement.style.setProperty() calls.
export const getCSSVars = (presetName, mode = 'light') =>
  getThemePreset(presetName)[mode] ?? {};
